/**
 * Runtime Events Contracts
 * Mission 008.5: persistent runtime event infrastructure contracts.
 */

import { CorrelationId, RuntimeContext, RuntimeExecutionId } from '../contracts'

export enum EventCategory {
  AUTHORIZATION = 'authorization',
  BUSINESS = 'business',
  SYSTEM = 'system',
  USER = 'user',
  RUNTIME = 'runtime',
  OPERATIONAL = 'operational',
  PERFORMANCE = 'performance',
  ACCOUNT_BALANCE = 'account_balance',
  ERROR = 'error',
  SECURITY = 'security'
}

export enum EventSource {
  RUNTIME_ENGINE = 'runtime_engine',
  APPLICATION_SERVICE = 'application_service',
  ROUTE_GUARD = 'route_guard',
  SECURITY_AUDIT = 'security_audit',
  API_GATEWAY = 'api_gateway',
  UI_COMPONENT = 'ui_component'
}

export enum RuntimeEventType {
  AUTHORIZATION_GRANTED = 'authorization_granted',
  AUTHORIZATION_DENIED = 'authorization_denied',
  AUTHORIZATION_DECISION = 'authorization_decision',
  AUTHENTICATION_ATTEMPT = 'authentication_attempt',
  SECURITY_VIOLATION = 'security_violation',

  RUNTIME_OPERATION_STARTED = 'runtime_operation_started',
  RUNTIME_OPERATION_COMPLETED = 'runtime_operation_completed',
  RUNTIME_OPERATION_FAILED = 'runtime_operation_failed',
  RUNTIME_HEALTH_CHECK = 'runtime_health_check',

  BUSINESS_OPERATION_START = 'business_operation_start',
  BUSINESS_OPERATION_COMPLETE = 'business_operation_complete',
  BUSINESS_OPERATION_ERROR = 'business_operation_error',
  BUSINESS_OPERATION_STARTED = 'business_operation_started',
  BUSINESS_OPERATION_COMPLETED = 'business_operation_completed',

  ACCOUNT_BALANCE_REQUESTED = 'account_balance_requested',
  ACCOUNT_BALANCE_RETURNED = 'account_balance_returned',
  ACCOUNT_BALANCE_FAILED = 'account_balance_failed',

  PERFORMANCE_METRIC = 'performance_metric',
  ERROR_OCCURRED = 'error_occurred',

  ROUTE_TRANSITIONED = 'route_transitioned',
  ROUTE_ENTERED = 'route_entered',
  ROUTE_EXITED = 'route_exited',
  ROUTE_DENIED = 'route_denied',
  ROUTE_SUSPENDED = 'route_suspended',

  BOUNDARY_REQUEST_STARTED = 'boundary.request.started',
  BOUNDARY_REQUEST_COMPLETED = 'boundary.request.completed',
  BOUNDARY_REQUEST_FAILED = 'boundary.request.failed',
  ADAPTER_PROVIDER_CALLED = 'adapter.provider.called',
  ADAPTER_PROVIDER_COMPLETED = 'adapter.provider.completed',
  ENTITY_PROJECTED = 'entity.projected'
}

export enum EventPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum RuntimeEventSensitivity {
  PUBLIC = 'public',
  OPERATIONAL = 'operational',
  SENSITIVE = 'sensitive',
  AUDIT = 'audit'
}

export interface EventMetadata {
  priority?: EventPriority
  tags?: string[]
  expiresAt?: Date
  version?: string
  stream?: string
  sensitivity?: RuntimeEventSensitivity
  custom?: Record<string, any>
}

export interface RuntimeEvent {
  id: string
  type: RuntimeEventType
  category: EventCategory
  timestamp: Date
  source: EventSource
  context: RuntimeContext
  data: Record<string, any>
  metadata?: EventMetadata
}

export interface PersistedRuntimeEvent extends RuntimeEvent {
  sequence: number
  stream: string
  severity: EventPriority
  sensitivity: RuntimeEventSensitivity
  expiresAt: Date | null
  persistedAt: Date
}

export type EventHandler = (event: PersistedRuntimeEvent) => void | Promise<void>

export interface EventFilter {
  types?: RuntimeEventType[]
  categories?: EventCategory[]
  sources?: EventSource[]
  priorities?: EventPriority[]
  streams?: string[]
  executionId?: RuntimeExecutionId
  correlationId?: CorrelationId
  actor?: string
  route?: string
  service?: string
  domain?: string
  afterSequence?: number
  from?: Date
  to?: Date
  customFilter?: (event: PersistedRuntimeEvent) => boolean
}

export interface EventQuery {
  filter?: EventFilter
  limit?: number
  offset?: number
  sortBy?: 'timestamp' | 'sequence' | 'type' | 'category' | 'priority'
  sortOrder?: 'asc' | 'desc'
}

export interface ReplayWindow {
  from?: Date
  to?: Date
  afterSequence?: number
  maxEvents?: number
}

export interface EventSubscription {
  id: string
  filter: EventFilter
  handler: EventHandler
  options?: EventSubscriptionOptions
  createdAt?: number // HARDENING P0: Track subscription creation time for cleanup
}

export interface EventSubscriptionOptions {
  replayOnSubscribe?: boolean
  maxReplayEvents?: number
  buffered?: boolean
  bufferSize?: number
  priority?: EventPriority
}

export interface EventPublishingOptions {
  synchronous?: boolean
  timeoutMs?: number
  persist?: boolean
}

export interface EventBusStatistics {
  totalPublished: number
  totalProcessed: number
  totalPersisted: number
  activeSubscriptions: number
  queueDepth: number
  errorCount: number
  replayCount: number
}

export interface RetentionPolicy {
  maxEvents: number
  defaultTtlMs: number
  categoryTtlMs: Partial<Record<EventCategory, number>>
  sensitivityTtlMs: Partial<Record<RuntimeEventSensitivity, number>>
}

export interface EventAggregationSnapshot {
  totalEvents: number
  byCategory: Partial<Record<EventCategory, number>>
  byPriority: Partial<Record<EventPriority, number>>
  byType: Record<string, number>
  byStream: Record<string, number>
  byExecutionId: Record<string, number>
  byCorrelationId: Record<string, number>
  lastUpdatedAt: Date | null
}
